import React, { FC, useEffect, useState, MouseEvent } from "react";
import styled from "styled-components";
import { marked } from "marked";
import { Entry } from "contentful";
import CTA, { CTAProps } from "./CTA";

marked.setOptions({
  breaks: true,
});

interface TextBlockProps extends Entry {
  brand: string,
  fields: {
    content: string;
    contentAlign: string;
    ctas?: [CTAProps];
    maxWidth?: number;
    paddingBottom?: number;
    paddingTop?: number;
    sideTextPadding?: string;
    style?: string;
    textAlign: string;
    hideOnDesktop?: boolean;
    hideOnMobile?: boolean;
    truncateText?: boolean;
    truncateCharacters?: number;
    expandText?: string;
    collapseText?: string;
  }
}

const AlignContainer = styled.div<{
  $contentAlign: string;
  $hideOnDesktop?: boolean;
  $hideOnMobile?: boolean;
}>`
  display: flex;
  flex-direction: column;

  ${({ $contentAlign }) => $contentAlign == 'left' && `
    align-items: flex-start;
  `}

  ${({ $contentAlign }) => $contentAlign == 'center' && `
    align-items: center;
  `}

  ${({ $contentAlign }) => $contentAlign == 'right' && `
    align-items: flex-end;
  `}

  ${({ $hideOnMobile }) =>
    $hideOnMobile &&
    `
      @media (max-width: 1023px) {
        display: none;
      }
    `}

  ${({ $hideOnDesktop }) =>
    $hideOnDesktop &&
    `
      @media (min-width: 1024px) {
        display: none;
      }
    `}
`;

interface ContainerProps {
  $maxWidth?: number;
  $paddingBottom?: number;
  $paddingTop?: number;
  $sideTextPadding?: string;
  $style?: string;
  $textAlign: string;
  $stackCTAs: boolean;
}

const Container = styled.div<ContainerProps>`
  position: relative;
  z-index: 2;
  max-width: ${({ $maxWidth }) => $maxWidth ? `${$maxWidth}px` : '100%'};
  text-align: ${({ $textAlign }) => $textAlign || 'left'};

  ${({ $paddingTop }) => $paddingTop && `
    padding-top: ${$paddingTop}px;
  `}

  ${({ $paddingBottom }) => $paddingBottom && `
    padding-bottom: ${$paddingBottom}px;
  `}

  ${({ $sideTextPadding }) => {
    switch ($sideTextPadding) {
      case 'Large Side Padding':
        return `
          padding-left: 1rem;
          padding-right: 1rem;

          @media (min-width: 1024px) {
            padding-left: 3rem;
            padding-right: 3rem;
          }
        `;
      case 'Small Side Padding':
        return `
          padding-left: 1rem;
          padding-right: 1rem;
        `;
      case 'No Side Padding':
      default:
        return `
          padding-left: 0rem;
          padding-right: 0rem;
        `;
    }
  }}

  ${({ $style }) => $style == 'Subscription Card' && `
    border: 2px solid #1a1b1b;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.02) 57%, rgba(255, 255, 255, 0.02) 58%);
    text-align: center;
    padding: 40px 30px 50px;
    border-radius: 30px;
    width: 100%;
    color: #fff;
    transition: all 0.5s;
    &:hover {
      border-color: #0083FF;
      background: linear-gradient(45deg, rgba(0, 131, 255, 0.11) 0%, rgba(251, 253, 255, 0.02) 57%, rgba(255, 255, 255, 0.02) 58%);
    }
  `}

  ${({ $style }) => $style == 'Grey Card' && `
    overflow: hidden;
    border-radius: 16px;
    background: #f5f5f5;
    padding: 3rem;

    @media (min-width: 768px) {
      padding: 4rem;
    }
  `}

  ${({ $style }) => $style == 'PDP Gray' && `
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    background: #F7F7F7;
    color: #191A1F;
    letter-spacing: 0.12px; 

    a {
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
    }
  `}

  ${({ $style }) => $style == 'PDP Green' && `
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    width: 100%;
    background: #E1f0EE;
    color: #191A1F;
    letter-spacing: 0.12px; 

      a {
        font-weight: 400;
        font-size: 12px;
        line-height: 18px;
      }
  `}

  ${({ $stackCTAs }) => $stackCTAs && `
    > a {
      display: block;
    }
  `}
`;

// Helper: Append the toggle link to the end of the HTML string.
function appendToggleLink(html: string, toggle: string) {
  const lastParagraph = /<\/p>\s*$/;
  if (html.match(lastParagraph)) {
    return html.replace(
      lastParagraph,
      ` <span class="truncate-toggle" style="cursor:pointer;text-decoration:underline;margin-left:0.25em;">${toggle}</span></p>`
    );
  } else {
    return html + ` <span class="truncate-toggle" style="cursor:pointer;text-decoration:underline;margin-left:0.25em;">${toggle}</span>`;
  }
}

const TextBlock: FC<TextBlockProps> = ({
  brand,
  fields: {
    content,
    contentAlign,
    ctas,
    maxWidth,
    paddingBottom,
    paddingTop,
    sideTextPadding,
    style,
    textAlign,
    hideOnDesktop,
    hideOnMobile,
    truncateText,
    truncateCharacters = 100,
    expandText = "Expand",
    collapseText = "Collapse",
  },
}) => {
  let allCTAsAreTextStyle = (ctas && ctas.length > 0) ? true : false;
  ctas?.forEach(cta => {
    if (!cta.fields.style.includes('Text')) {
      allCTAsAreTextStyle = false;
    }
  });

  const shouldTruncate = truncateText && (content.length > truncateCharacters);
  const truncatedRaw = content.substring(0, truncateCharacters) + "...";

  const [fullHtml, setFullHtml] = useState<string>("");
  const [truncatedHtml, setTruncatedHtml] = useState<string>("");

  useEffect(() => {
    const parseContent = async () => {
      const parsedFull = await marked.parse(content);
      setFullHtml(parsedFull);
      if (shouldTruncate) {
        setTruncatedHtml(await marked.parse(truncatedRaw));
      }
    };
    parseContent();
  }, [content, truncatedRaw, shouldTruncate]);

  const [expanded, setExpanded] = useState(false);

  const finalHtml = shouldTruncate
    ? (expanded ? appendToggleLink(fullHtml, collapseText) : appendToggleLink(truncatedHtml, expandText))
    : fullHtml;

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("truncate-toggle")) {
      setExpanded(prev => !prev);
    }
  };

  return (
    <AlignContainer
      $contentAlign={contentAlign}
      $hideOnDesktop={hideOnDesktop}
      $hideOnMobile={hideOnMobile}
    >
      <Container
        $maxWidth={maxWidth}
        $paddingBottom={paddingBottom}
        $paddingTop={paddingTop}
        $sideTextPadding={sideTextPadding}
        $style={style}
        $textAlign={textAlign}
        $stackCTAs={allCTAsAreTextStyle}
      >
        <div onClick={handleClick} dangerouslySetInnerHTML={{ __html: finalHtml }} />
        {ctas?.map((cta) => (
          <CTA key={cta.sys.id} {...cta} brand={brand} />
        ))}
      </Container>
    </AlignContainer>
  );
}

export default TextBlock;